import { useEffect, useRef, useState } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

const FADE_DURATION_MS = 800;

export const useDigitalCardMusicPlayback = (enabled: boolean) => {
  const audioRef = useRef<HTMLAudioElement>();
  const fadeRef = useRef<ReturnType<typeof setInterval>>();
  const [isPlaying, setIsPlaying] = useState(false);

  // No state update here: it also runs on unmount.
  const releaseAudio = useEventCallback(() => {
    clearInterval(fadeRef.current);
    fadeRef.current = undefined;
    const audio = audioRef.current;
    audioRef.current = undefined;
    audio?.pause();
  });

  const stopMusic = () => {
    releaseAudio();
    setIsPlaying(false);
  };

  useEffect(() => releaseAudio, [enabled, releaseAudio]);

  const playMusic = (url: string | undefined) => {
    stopMusic();
    if (!enabled || !url) return;

    const audio = new Audio(url);
    audio.loop = false;
    audioRef.current = audio;
    // Follow the element rather than our own calls: the browser can refuse play() or end the track.
    const sync = () => {
      if (audioRef.current === audio) setIsPlaying(!audio.paused);
    };
    audio.addEventListener('play', sync);
    audio.addEventListener('pause', sync);
    audio.addEventListener('ended', sync);
    // Called directly from the click handler to preserve user activation.
    void audio.play().catch(sync);
  };

  /** Pauses the current track, or resumes it — from the start once it has ended. */
  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio || fadeRef.current !== undefined) return;

    if (!audio.paused) {
      audio.pause();
      return;
    }
    if (audio.ended) audio.currentTime = 0;
    // A newer track, or an unmount, may have replaced this one by the time play() rejects.
    void audio.play().catch(() => {
      if (audioRef.current === audio) setIsPlaying(false);
    });
  };

  const fadeOutMusic = () => {
    const audio = audioRef.current;
    if (!audio || fadeRef.current !== undefined) return;

    const startedAt = performance.now();
    const initialVolume = audio.volume;
    fadeRef.current = setInterval(() => {
      const progress = Math.min((performance.now() - startedAt) / FADE_DURATION_MS, 1);
      audio.volume = initialVolume * (1 - progress);
      if (progress === 1) stopMusic();
    }, 25);
  };

  return { playMusic, fadeOutMusic, togglePlayback, isPlaying };
};
